import {
  type Code1cRow,
  DICT_1C_KINDS,
  type Dict1cKind,
  type Dict1cUploadResult,
  DOC_KINDS,
  EXPORT_1C_MAX_IDS,
  EXPORT_STATUSES,
  type Export1cDocument,
  type ExportStatus,
  type Reconcile1cResult,
  seesMoney,
} from '@epc/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { Download, FileUp } from 'lucide-react';
import { useRef, useState } from 'react';
import { api } from '../../lib/api';
import { date, tenge, todayLocal } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';

/** Строки сверки без своего идентификатора: ключ — порядковый номер, посчитанный заранее */
function keyed<T>(rows: T[]): Array<{ row: T; key: string }> {
  return rows.map((row, n) => ({ row, key: String(n) }));
}

const STATUS_TONE: Record<ExportStatus, 'warning' | 'info' | 'success'> = {
  to_export: 'warning',
  exported: 'info',
  posted_1c: 'success',
};

const docsQuery = (q: Record<string, string | undefined>) =>
  queryOptions({
    queryKey: ['wh', '1c', 'documents', q],
    queryFn: () => api<Export1cDocument[]>('/warehouse/1c/documents', { query: q }),
  });
const codesQuery = (kind: Code1cRow['kind'], q: string) =>
  queryOptions({
    queryKey: ['wh', '1c', 'codes', kind, q],
    queryFn: () => api<Code1cRow[]>('/warehouse/1c/codes', { query: { kind, q: q || undefined } }),
  });
const dictQuery = queryOptions({
  queryKey: ['wh', '1c', 'dict'],
  queryFn: () => api<Array<{ kind: Dict1cKind; rows: number }>>('/warehouse/1c/dict'),
});

/** Обмен с 1С (СК-35): документы к выгрузке, коды 1С, справочники для подсказки кодов, сверка поступлений */
export function Exchange1cTab() {
  const [section, setSection] = useState<'docs' | 'codes' | 'dict' | 'reconcile'>('docs');
  const sections = [
    ['docs', 'Документы'],
    ['codes', 'Коды 1С'],
    ['dict', 'Справочники 1С'],
    ['reconcile', 'Сверка поступлений'],
  ] as const;
  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Обмен с 1С">
        {sections.map(([k, t]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={section === k}
            onClick={() => setSection(k)}
            className={clsx(
              'h-11 rounded-md border px-4 text-[13px] font-medium',
              section === k
                ? 'border-ink bg-ink text-on-ink'
                : 'border-line bg-surface text-ink-2 hover:text-ink',
            )}
          >
            {t}
          </button>
        ))}
      </div>
      {section === 'docs' ? <DocsSection /> : null}
      {section === 'codes' ? <CodesSection /> : null}
      {section === 'dict' ? <DictSection /> : null}
      {section === 'reconcile' ? <ReconcileSection /> : null}
    </>
  );
}

/* ───────────────────────── Документы ───────────────────────── */

function DocsSection() {
  const qc = useQueryClient();
  const me = useMe();
  // Файл выгрузки содержит цены: ссылки показываются только с уровнем денег «Склад»
  const money = seesMoney(me.access, 'warehouse');
  const [from, setFrom] = useState(`${todayLocal().slice(0, 7)}-01`);
  const [to, setTo] = useState(todayLocal());
  const [status, setStatus] = useState<ExportStatus | ''>('to_export');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [posting, setPosting] = useState<Export1cDocument | null>(null);
  const q = { from: from || undefined, to: to || undefined, status: status || undefined };
  const r = useQuery(docsQuery(q));
  const rows = r.data ?? [];
  const picked = rows.filter((d) => selected.has(d.id));
  const tooMany = picked.length > EXPORT_1C_MAX_IDS;
  const qs = new URLSearchParams(
    picked.length
      ? { ids: picked.map((d) => d.id).join(',') }
      : Object.fromEntries(Object.entries(q).filter((e): e is [string, string] => Boolean(e[1]))),
  ).toString();
  const mark = useMutation({
    mutationFn: (set: 'exported' | 'to_export') =>
      api<{ updated: number }>('/warehouse/1c/mark', {
        body: picked.length ? { ids: picked.map((d) => d.id), set } : { filter: q, set },
      }),
    onSuccess: (res, set) => {
      toast(
        set === 'exported'
          ? `Отмечено выгруженными: ${res.updated}`
          : `Возвращено к выгрузке: ${res.updated}`,
      );
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['wh'] });
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const hasAmount = rows.some((d) => d.amount !== null);
  const toggleAll = () =>
    setSelected(selected.size === rows.length ? new Set() : new Set(rows.map((d) => d.id)));
  return (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="С">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="По">
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <Field label="Статус 1С">
          <Select value={status} onChange={(e) => setStatus(e.target.value as ExportStatus | '')}>
            <option value="">Все</option>
            {(Object.keys(EXPORT_STATUSES) as ExportStatus[]).map((k) => (
              <option key={k} value={k}>
                {EXPORT_STATUSES[k]}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Panel
        title="Документы для 1С"
        meta={
          tooMany
            ? `выбрано ${picked.length}: для файла по ссылке — не больше ${EXPORT_1C_MAX_IDS}, снимите выбор или сузьте отбор`
            : picked.length
              ? `выбрано ${picked.length} из ${rows.length}`
              : `${rows.length} документов — в файл попадут все по отбору`
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {money && !tooMany ? (
              <>
                <a
                  href={`/api/v1/warehouse/1c/export.xlsx${qs ? `?${qs}` : ''}`}
                  className="inline-flex h-11 items-center gap-2 rounded-md border border-line-strong bg-surface px-4 text-[14px] font-medium text-ink hover:underline"
                >
                  <Download className="size-4" aria-hidden />
                  Excel
                </a>
                <a
                  href={`/api/v1/warehouse/1c/export.csv${qs ? `?${qs}` : ''}`}
                  className="inline-flex h-11 items-center gap-2 rounded-md border border-line-strong bg-surface px-4 text-[14px] font-medium text-ink hover:underline"
                >
                  <Download className="size-4" aria-hidden />
                  CSV
                </a>
              </>
            ) : null}
            <Button
              variant="primary"
              className="min-h-11"
              loading={mark.isPending}
              disabled={!rows.length}
              onClick={() => mark.mutate('exported')}
            >
              Отметить выгруженными
            </Button>
          </div>
        }
        bodyClassName="p-0"
      >
        {r.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : r.isError ? (
          <ErrorBox error={r.error} className="m-5" />
        ) : !rows.length ? (
          <div className="p-5">
            <EmptyState title="Пусто">По отбору документов нет.</EmptyState>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-line md:hidden">
              {rows.map((d) => (
                <li key={d.id} className="flex flex-col gap-1 px-4 py-3 text-[13px]">
                  <div className="flex items-center justify-between gap-2">
                    <Checkbox
                      label={
                        <Link
                          to="/warehouse/documents/$docId"
                          params={{ docId: d.id }}
                          className="font-medium text-ink"
                        >
                          {d.number}
                        </Link>
                      }
                      checked={selected.has(d.id)}
                      onChange={() => {
                        const s = new Set(selected);
                        if (s.has(d.id)) s.delete(d.id);
                        else s.add(d.id);
                        setSelected(s);
                      }}
                    />
                    <Badge tone={STATUS_TONE[d.exportStatus]}>{EXPORT_STATUSES[d.exportStatus]}</Badge>
                  </div>
                  <span className="text-ink-2">
                    {DOC_KINDS[d.kind]?.title} · {date(d.docDate)} · {d.warehouseName}
                    {d.counterpartyName ? ` · ${d.counterpartyName}` : ''}
                  </span>
                  {d.amount !== null ? <span className="num text-ink">{tenge(d.amount)}</span> : null}
                  {d.missing.length ? (
                    <span className="text-danger">Не хватает: {d.missing.join(', ')}</span>
                  ) : null}
                  {d.number1c ? (
                    <span className="text-ink-2">
                      1С: {d.number1c}
                      {d.date1c ? ` от ${date(d.date1c)}` : ''}
                    </span>
                  ) : null}
                  {d.canOperate ? (
                    <Button className="min-h-11 self-start" onClick={() => setPosting(d)}>
                      {d.number1c ? 'Изменить номер 1С' : 'Проведён в 1С'}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
            <ScrollX className="hidden md:block">
              <table className="w-full min-w-[1000px] text-[13px]">
                <thead>
                  <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                    <th className="py-2.5 pl-5 pr-3">
                      <input
                        type="checkbox"
                        aria-label="Выбрать все"
                        className="size-4"
                        checked={rows.length > 0 && selected.size === rows.length}
                        onChange={toggleAll}
                      />
                    </th>
                    <th className="py-2.5 pr-3 font-medium">Документ</th>
                    <th className="py-2.5 pr-3 font-medium">Дата</th>
                    <th className="py-2.5 pr-3 font-medium">Склад</th>
                    <th className="py-2.5 pr-3 font-medium">Контрагент / МОЛ</th>
                    {hasAmount ? <th className="py-2.5 pr-3 text-right font-medium">Сумма</th> : null}
                    <th className="py-2.5 pr-3 font-medium">Статус 1С</th>
                    <th className="py-2.5 pr-3 font-medium">Документ 1С</th>
                    <th className="py-2.5 pr-3 font-medium">Не хватает</th>
                    <th className="py-2.5 pr-5" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((d) => (
                    <tr key={d.id} className="border-t border-line">
                      <td className="py-2 pl-5 pr-3">
                        <input
                          type="checkbox"
                          aria-label={`Выбрать ${d.number}`}
                          className="size-4"
                          checked={selected.has(d.id)}
                          onChange={() => {
                            const s = new Set(selected);
                            if (s.has(d.id)) s.delete(d.id);
                            else s.add(d.id);
                            setSelected(s);
                          }}
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <Link
                          to="/warehouse/documents/$docId"
                          params={{ docId: d.id }}
                          className="font-medium text-ink hover:underline"
                        >
                          {d.number}
                        </Link>
                        <div className="text-[12px] text-ink-2">
                          {DOC_KINDS[d.kind]?.title}
                          {d.intergroup ? ' · внутригрупповая' : ''}
                        </div>
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">{date(d.docDate)}</td>
                      <td className="py-2 pr-3">
                        {d.warehouseName}
                        {d.warehouseCode ? (
                          <span className="num ml-1 text-ink-2">{d.warehouseCode}</span>
                        ) : null}
                        {d.toWarehouseName ? (
                          <div className="text-[12px] text-ink-2">→ {d.toWarehouseName}</div>
                        ) : null}
                      </td>
                      <td className="py-2 pr-3">
                        {d.counterpartyName ?? d.molName ?? '—'}
                        {(d.counterpartyCode ?? d.molCode) ? (
                          <span className="num ml-1 text-ink-2">{d.counterpartyCode ?? d.molCode}</span>
                        ) : null}
                      </td>
                      {hasAmount ? (
                        <td className="num py-2 pr-3 text-right">
                          {d.amount === null ? '—' : tenge(d.amount)}
                        </td>
                      ) : null}
                      <td className="py-2 pr-3">
                        <Badge tone={STATUS_TONE[d.exportStatus]}>{EXPORT_STATUSES[d.exportStatus]}</Badge>
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {d.number1c ? `${d.number1c}${d.date1c ? ` от ${date(d.date1c)}` : ''}` : '—'}
                      </td>
                      <td className="py-2 pr-3 text-danger">{d.missing.join(', ') || ''}</td>
                      <td className="py-2 pr-5 text-right">
                        {d.canOperate ? (
                          <Button size="sm" className="min-h-11" onClick={() => setPosting(d)}>
                            {d.number1c ? 'Номер 1С' : 'Проведён в 1С'}
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
            {picked.length ? (
              <div className="border-t border-line px-5 py-3">
                <Button
                  className="min-h-11"
                  loading={mark.isPending}
                  onClick={() => mark.mutate('to_export')}
                >
                  Вернуть выбранные к выгрузке
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Panel>
      <p className="mt-3 text-[12px] text-ink-2">
        Файл подходит для «Загрузки данных из табличного документа» в 1С: одна строка на позицию, коды склада,
        номенклатуры, контрагента, МОЛ и договора. Бухгалтерская стоимость, ЭСФ, СНТ и амортизация остаются в
        1С (СК-34).
      </p>
      {posting ? <Posted1cDialog doc={posting} onClose={() => setPosting(null)} /> : null}
    </>
  );
}

function Posted1cDialog({ doc, onClose }: { doc: Export1cDocument; onClose: () => void }) {
  const qc = useQueryClient();
  const [number1c, setNumber] = useState(doc.number1c ?? '');
  const [date1c, setDate] = useState(doc.date1c ?? doc.docDate);
  const m = useMutation({
    mutationFn: () =>
      api<Export1cDocument>(`/warehouse/1c/documents/${doc.id}`, {
        method: 'PATCH',
        body: { number1c: number1c.trim() || null, date1c: date1c || null },
      }),
    onSuccess: (d) => {
      toast(d.number1c ? `${d.number} проведён в 1С` : 'Отметка 1С снята');
      qc.invalidateQueries({ queryKey: ['wh'] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`${doc.number}: документ 1С`}
      description="Номер и дата документа, которым он проведён в 1С. Пустой номер снимает отметку."
      footer={
        <>
          <Button className="min-h-11" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" className="min-h-11" loading={m.isPending} onClick={() => m.mutate()}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Номер документа 1С">
          <Input value={number1c} onChange={(e) => setNumber(e.target.value)} autoFocus />
        </Field>
        <Field label="Дата документа 1С">
          <Input type="date" value={date1c} onChange={(e) => setDate(e.target.value)} />
        </Field>
      </div>
      {m.isError ? <ErrorBox error={m.error} className="mt-3" /> : null}
    </Dialog>
  );
}

/* ───────────────────────── Коды 1С ───────────────────────── */

const CODE_KINDS: Array<{ key: Code1cRow['kind']; title: string; hint: string }> = [
  { key: 'user', title: 'МОЛ', hint: 'код сотрудника в 1С — материально ответственные лица складов' },
  {
    key: 'counterparty',
    title: 'Контрагенты',
    hint: 'код контрагента в 1С; по БИН подставляется из справочника',
  },
  { key: 'contract', title: 'Договоры', hint: 'код договора контрагента в 1С' },
];

function CodesSection() {
  const qc = useQueryClient();
  const [kind, setKind] = useState<Code1cRow['kind']>('counterparty');
  const [q, setQ] = useState('');
  const r = useQuery(codesQuery(kind, q));
  const [edits, setEdits] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (row: { refId: string; code: string }) =>
      api<Code1cRow>('/warehouse/1c/codes', {
        method: 'PUT',
        body: { kind, refId: row.refId, code: row.code || null },
      }),
    onSuccess: (res) => {
      toast(res.code ? 'Код записан' : 'Код убран');
      setEdits((e) => {
        const n = { ...e };
        delete n[res.refId];
        return n;
      });
      qc.invalidateQueries({ queryKey: ['wh', '1c'] });
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const spec = CODE_KINDS.find((k) => k.key === kind)!;
  return (
    <Panel
      title="Коды 1С"
      meta={spec.hint}
      actions={
        <div className="flex flex-wrap gap-2">
          {CODE_KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              onClick={() => {
                setKind(k.key);
                setEdits({});
              }}
              className={clsx(
                'h-11 rounded-md border px-3 text-[13px] font-medium',
                kind === k.key ? 'border-ink bg-ink text-on-ink' : 'border-line bg-surface text-ink-2',
              )}
            >
              {k.title}
            </button>
          ))}
        </div>
      }
      bodyClassName="p-0"
    >
      <div className="border-b border-line p-4">
        <Input placeholder="Поиск по названию или коду" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {r.isPending ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : r.isError ? (
        <ErrorBox error={r.error} className="m-5" />
      ) : !r.data.length ? (
        <div className="p-5">
          <EmptyState title="Пусто">Записей нет.</EmptyState>
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {r.data.map((row) => {
            const val = edits[row.refId] ?? row.code ?? '';
            const dirty = (edits[row.refId] ?? row.code ?? '') !== (row.code ?? '');
            return (
              <li key={row.refId} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center">
                <span className="flex-1 text-[14px] text-ink">{row.name}</span>
                <div className="flex gap-2">
                  <Input
                    aria-label={`Код 1С: ${row.name}`}
                    className="w-40"
                    value={val}
                    placeholder="код 1С"
                    onChange={(e) => setEdits({ ...edits, [row.refId]: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && dirty) save.mutate({ refId: row.refId, code: val.trim() });
                    }}
                  />
                  <Button
                    className="min-h-11"
                    disabled={!dirty}
                    loading={save.isPending && save.variables?.refId === row.refId}
                    onClick={() => save.mutate({ refId: row.refId, code: val.trim() })}
                  >
                    Сохранить
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

/* ───────────────────────── Справочники ───────────────────────── */

function DictSection() {
  const qc = useQueryClient();
  const counts = useQuery(dictQuery);
  const [kind, setKind] = useState<Dict1cKind>('nomenclature');
  const [last, setLast] = useState<Dict1cUploadResult | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  const up = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('kind', kind);
      form.append('file', file);
      return api<Dict1cUploadResult>('/warehouse/1c/dict', { form });
    },
    onSuccess: (res) => {
      setLast(res);
      toast(`Загружено строк: ${res.rows}${res.matched ? `, подставлено кодов: ${res.matched}` : ''}`);
      qc.invalidateQueries({ queryKey: ['wh'] });
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  return (
    <Panel
      title="Справочники 1С"
      meta="выгрузка из 1С в Excel или CSV: колонки «Код», «Наименование», для контрагентов — «БИН», для номенклатуры — «Ед.»"
    >
      <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
        <Field label="Справочник">
          <Select value={kind} onChange={(e) => setKind(e.target.value as Dict1cKind)}>
            {(Object.keys(DICT_1C_KINDS) as Dict1cKind[]).map((k) => (
              <option key={k} value={k}>
                {DICT_1C_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        <input
          ref={ref}
          type="file"
          accept=".xlsx,.csv,.txt"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) up.mutate(f);
            e.target.value = '';
          }}
        />
        <Button
          variant="primary"
          className="min-h-11"
          icon={<FileUp className="size-4" />}
          loading={up.isPending}
          onClick={() => ref.current?.click()}
        >
          Загрузить файл
        </Button>
      </div>
      {last ? (
        <p className="mt-3 text-[13px] text-ink-2">
          {DICT_1C_KINDS[last.kind]}: {last.rows} строк
          {last.matched ? `, коды подставлены ${last.matched} записям портала` : ''}.
        </p>
      ) : null}
      <ul className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {(counts.data ?? []).map((c) => (
          <li
            key={c.kind}
            className="flex items-center justify-between rounded-md border border-line px-4 py-3 text-[13px]"
          >
            <span>{DICT_1C_KINDS[c.kind]}</span>
            <span className="num text-ink-2">{c.rows}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[12px] text-ink-2">
        Коды контрагентов подставляются по БИН, складов — по названию, номенклатуры — при полном совпадении
        названия (и единицы, если она есть в файле); если в файле несколько записей с одним БИН или названием,
        код выбирает человек. Остальное подсказывается в карточке позиции и в «Кодах 1С».
      </p>
    </Panel>
  );
}

/* ───────────────────────── Сверка ───────────────────────── */

function ReconcileSection() {
  const [from, setFrom] = useState(`${todayLocal().slice(0, 7)}-01`);
  const [to, setTo] = useState(todayLocal());
  const [res, setRes] = useState<Reconcile1cResult | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  const m = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('from', from);
      form.append('to', to);
      form.append('file', file);
      return api<Reconcile1cResult>('/warehouse/1c/reconcile', { form });
    },
    onSuccess: (r) => setRes(r),
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const RES_TONE = { ok: 'success', amount: 'warning', missing: 'danger' } as const;
  const RES_TEXT = { ok: 'сходится', amount: 'сумма расходится', missing: 'в портале нет' } as const;
  return (
    <>
      <Panel
        title="Сверка поступлений с 1С"
        meta="файл из 1С: «Контрагент» или «БИН», «Номер», «Дата», «Сумма» — по каждой строке ищется поступление портала"
      >
        <div className="grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <Field label="Поступления портала с">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="по">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <input
            ref={ref}
            type="file"
            accept=".xlsx,.csv,.txt"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) m.mutate(f);
              e.target.value = '';
            }}
          />
          <Button
            variant="primary"
            className="min-h-11"
            icon={<FileUp className="size-4" />}
            loading={m.isPending}
            onClick={() => ref.current?.click()}
          >
            Сверить файл
          </Button>
        </div>
      </Panel>
      {res ? (
        <>
          <Panel
            className="mt-4"
            title="Строки файла 1С"
            meta={`${res.rows.length}: сходится ${res.rows.filter((r) => r.result === 'ok').length}, сумма расходится ${res.rows.filter((r) => r.result === 'amount').length}, в портале нет ${res.rows.filter((r) => r.result === 'missing').length}`}
            bodyClassName="p-0"
          >
            {res.rows.length ? (
              <ul className="divide-y divide-line">
                {keyed(res.rows).map(({ row: r, key }) => (
                  <li
                    key={key}
                    className="flex flex-col gap-1 px-4 py-3 text-[13px] sm:flex-row sm:items-center sm:gap-4"
                  >
                    <span className="flex-1">
                      <span className="font-medium text-ink">{r.counterparty}</span>
                      <span className="text-ink-2">
                        {' '}
                        · № {r.number || '—'}
                        {r.date ? ` от ${date(r.date)}` : ''}
                        {r.amount !== null ? ` · ${tenge(r.amount)}` : ''}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      {r.docId ? (
                        <Link
                          to="/warehouse/documents/$docId"
                          params={{ docId: r.docId }}
                          className="text-ink hover:underline"
                        >
                          {r.docNumber}
                          {r.docAmount !== null ? ` · ${tenge(r.docAmount)}` : ''}
                        </Link>
                      ) : null}
                      <Badge tone={RES_TONE[r.result]}>{RES_TEXT[r.result]}</Badge>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-5">
                <EmptyState title="Пусто">В файле нет строк со строками поступлений.</EmptyState>
              </div>
            )}
          </Panel>
          <Panel
            className="mt-4"
            title="Есть в портале, нет в файле"
            meta={`${res.onlyInPortal.length}`}
            bodyClassName="p-0"
          >
            {res.onlyInPortal.length ? (
              <ul className="divide-y divide-line">
                {res.onlyInPortal.map((d) => (
                  <li
                    key={d.docId}
                    className="flex flex-col gap-1 px-4 py-3 text-[13px] sm:flex-row sm:items-center sm:gap-4"
                  >
                    <Link
                      to="/warehouse/documents/$docId"
                      params={{ docId: d.docId }}
                      className="font-medium text-ink hover:underline"
                    >
                      {d.docNumber}
                    </Link>
                    <span className="flex-1 text-ink-2">
                      {date(d.docDate)} · {d.counterparty ?? '—'}
                      {d.basis ? ` · ${d.basis}` : ''}
                    </span>
                    {d.amount !== null ? <span className="num text-ink">{tenge(d.amount)}</span> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-5">
                <EmptyState title="Всё сошлось">Все поступления за период есть в файле.</EmptyState>
              </div>
            )}
          </Panel>
        </>
      ) : null}
    </>
  );
}
